import {
    FeatureRollbackTimelineItemKind,
    type FeatureRollbackDatumTimelineInput,
    type FeatureRollbackFeatureTimelineInput,
    type FeatureRollbackTimelineBuildInput,
    type FeatureRollbackTimelineItem,
} from './FeatureRollbackTypes';

/**
 * FeatureManager 排序候选；先按统一 order，再按稳定序号破同分。
 */
interface FeatureRollbackTimelineCandidate {
    /** 统一创建顺序刻度。 */
    readonly order: number;
    /** 同 order 下的稳定序号。 */
    readonly sequence: number;
    /** 最终时间线节点。 */
    readonly item: FeatureRollbackTimelineItem;
}

/**
 * 创建特征退回序列节点。
 *
 * @param featureId 特征 ID。
 * @returns FeatureManager 退回序列特征节点。
 */
export function createFeatureRollbackFeatureTimelineItem(featureId: string): FeatureRollbackTimelineItem {
    return {
        kind: FeatureRollbackTimelineItemKind.Feature,
        id: featureId,
    };
}

/**
 * 创建顶级草图退回序列节点。
 *
 * @param sketchId 草图 ID。
 * @returns FeatureManager 退回序列草图节点。
 */
function createFeatureRollbackSketchTimelineItem(sketchId: string): FeatureRollbackTimelineItem {
    return {
        kind: FeatureRollbackTimelineItemKind.Sketch,
        id: sketchId,
    };
}

/**
 * 创建用户基准面退回序列节点。
 *
 * @param planeId 基准面 ID（字符串）。
 * @returns FeatureManager 退回序列基准面节点。
 */
function createFeatureRollbackDatumPlaneTimelineItem(planeId: string): FeatureRollbackTimelineItem {
    return {
        kind: FeatureRollbackTimelineItemKind.DatumPlane,
        id: planeId,
    };
}

/**
 * 创建用户基准轴退回序列节点。
 *
 * @param axisId 基准轴 ID（字符串）。
 * @returns FeatureManager 退回序列基准轴节点。
 */
function createFeatureRollbackDatumAxisTimelineItem(axisId: string): FeatureRollbackTimelineItem {
    return {
        kind: FeatureRollbackTimelineItemKind.DatumAxis,
        id: axisId,
    };
}

/**
 * 创建用户基准点退回序列节点。
 *
 * @param pointId 基准点 ID（字符串）。
 * @returns FeatureManager 退回序列基准点节点。
 */
function createFeatureRollbackDatumPointTimelineItem(pointId: string): FeatureRollbackTimelineItem {
    return {
        kind: FeatureRollbackTimelineItemKind.DatumPoint,
        id: pointId,
    };
}

/**
 * 判断特征是否消费指定草图。
 *
 * @param feature 特征摘要。
 * @param sketchId 草图 ID。
 * @returns 特征来源草图命中时返回 true。
 */
function isFeatureConsumingSketch(
    feature: FeatureRollbackFeatureTimelineInput,
    sketchId: string,
): boolean {
    return feature.sourceSketchId === sketchId;
}

/**
 * 向候选列表追加一条时间线节点。
 *
 * @param candidates 候选列表。
 * @param order 统一顺序。
 * @param item 时间线节点。
 */
function pushTimelineCandidate(
    candidates: FeatureRollbackTimelineCandidate[],
    order: number,
    item: FeatureRollbackTimelineItem,
): void {
    candidates.push({
        order,
        sequence: candidates.length,
        item,
    });
}

/**
 * 将用户基准几何追加为时间线候选。
 *
 * @param candidates 候选列表。
 * @param datumPlanes 用户基准面。
 * @param datumAxes 用户基准轴。
 * @param datumPoints 用户基准点。
 */
function appendDatumTimelineCandidates(
    candidates: FeatureRollbackTimelineCandidate[],
    datumPlanes: readonly FeatureRollbackDatumTimelineInput[],
    datumAxes: readonly FeatureRollbackDatumTimelineInput[],
    datumPoints: readonly FeatureRollbackDatumTimelineInput[],
): void {
    datumPlanes
        .slice()
        .sort((left, right) => left.order - right.order)
        .forEach((plane) => {
            pushTimelineCandidate(
                candidates,
                plane.order,
                createFeatureRollbackDatumPlaneTimelineItem(plane.id),
            );
        });
    datumAxes
        .slice()
        .sort((left, right) => left.order - right.order)
        .forEach((axis) => {
            pushTimelineCandidate(
                candidates,
                axis.order,
                createFeatureRollbackDatumAxisTimelineItem(axis.id),
            );
        });
    datumPoints
        .slice()
        .sort((left, right) => left.order - right.order)
        .forEach((point) => {
            pushTimelineCandidate(
                candidates,
                point.order,
                createFeatureRollbackDatumPointTimelineItem(point.id),
            );
        });
}

/**
 * 按统一 order 输出最终时间线。
 *
 * @param candidates 候选列表。
 * @returns FeatureManager 退回控制棒线性序列。
 */
function finalizeTimelineCandidates(
    candidates: readonly FeatureRollbackTimelineCandidate[],
): FeatureRollbackTimelineItem[] {
    return candidates
        .slice()
        .sort((left, right) => left.order - right.order || left.sequence - right.sequence)
        .map((candidate) => candidate.item);
}

/**
 * 按完整命令历史校正时间线中的特征节点顺序。
 *
 * 草图和用户基准几何负责确定 FeatureManager 中的显示槽位，但不能反向重排特征命令。
 * 这里仅替换特征槽位中的 ID，保留草图、基准面、基准轴和基准点的统一 order 位置。
 *
 * @param timelineItems 已按统一 order 排序的时间线节点。
 * @param orderedFeatures 按命令历史排序的特征摘要。
 * @returns 同时保留非特征槽位和特征命令顺序的时间线。
 */
function alignFeatureTimelineItemsWithCommandOrder(
    timelineItems: readonly FeatureRollbackTimelineItem[],
    orderedFeatures: readonly FeatureRollbackFeatureTimelineInput[],
): FeatureRollbackTimelineItem[] {
    const orderedFeatureItems = orderedFeatures.map((feature) => (
        createFeatureRollbackFeatureTimelineItem(feature.id)
    ));
    let nextFeatureItemIndex = 0;

    return timelineItems.map((item) => {
        if (item.kind !== FeatureRollbackTimelineItemKind.Feature) {
            return item;
        }

        const orderedFeatureItem = orderedFeatureItems[nextFeatureItemIndex];
        nextFeatureItemIndex += 1;
        return orderedFeatureItem ?? item;
    });
}

/**
 * 构建 FeatureManager 退回控制棒线性序列。
 *
 * 设计说明：
 * 1. 退回控制棒序列属于 FeatureManager 显示语义，必须同时包含特征、未吸收顶级草图与用户基准面/轴/点；
 * 2. 被特征消费的草图不作为顶级节点出现，而是由消费它的特征代表该位置；
 * 3. 用户基准几何与特征/草图共用 `order` 刻度混排，默认三基准面与原点不得进入输入；
 * 4. 该函数只返回 plain data，避免 Controller 与投影层重复维护同一套排序规则。
 *
 * @param input 特征、草图与用户基准摘要。
 * @returns FeatureManager 退回控制棒线性序列。
 */
export function createFeatureRollbackTimeline(
    input: FeatureRollbackTimelineBuildInput,
): FeatureRollbackTimelineItem[] {
    const datumPlanes = input.datumPlanes ?? [];
    const datumAxes = input.datumAxes ?? [];
    const datumPoints = input.datumPoints ?? [];
    const candidates: FeatureRollbackTimelineCandidate[] = [];

    if (input.sketches.length === 0) {
        input.features
            .slice()
            .sort((left, right) => left.order - right.order)
            .forEach((feature) => {
                pushTimelineCandidate(
                    candidates,
                    feature.order,
                    createFeatureRollbackFeatureTimelineItem(feature.id),
                );
            });
        appendDatumTimelineCandidates(candidates, datumPlanes, datumAxes, datumPoints);
        return finalizeTimelineCandidates(candidates);
    }

    const placedFeatureIds = new Set<string>();
    const orderedFeatures = input.features.slice().sort((left, right) => left.order - right.order);
    const orderedSketches = input.sketches.slice().sort((left, right) => left.order - right.order);

    orderedSketches.forEach((sketch) => {
        const consumingFeatures = orderedFeatures.filter((feature) => isFeatureConsumingSketch(feature, sketch.id));

        if (consumingFeatures.length > 0) {
            consumingFeatures.forEach((feature) => {
                /**
                 * 吸收草图由特征占位时，位置仍锚定草图创建序，
                 * 这样同一草图驱动的特征不会被用户基准几何拆散到错误时刻。
                 */
                pushTimelineCandidate(
                    candidates,
                    sketch.order,
                    createFeatureRollbackFeatureTimelineItem(feature.id),
                );
                placedFeatureIds.add(feature.id);
            });
            return;
        }

        pushTimelineCandidate(
            candidates,
            sketch.order,
            createFeatureRollbackSketchTimelineItem(sketch.id),
        );
    });

    /**
     * 非草图驱动特征或来源草图已不存在的特征仍必须保留在退回序列中。
     * 这里按特征自身 order 入候选，再与基准几何统一排序。
     */
    orderedFeatures
        .filter((feature) => !placedFeatureIds.has(feature.id))
        .forEach((feature) => {
            pushTimelineCandidate(
                candidates,
                feature.order,
                createFeatureRollbackFeatureTimelineItem(feature.id),
            );
        });

    appendDatumTimelineCandidates(candidates, datumPlanes, datumAxes, datumPoints);
    return alignFeatureTimelineItemsWithCommandOrder(
        finalizeTimelineCandidates(candidates),
        input.features,
    );
}
